import { GridOptions, ColGroupDef, ColDef, ValueFormatterParams } from 'ag-grid-community'
import { AmountFormat, CurrencyTradingLimitDetail, TradingEntityMetaDetail } from 'api'
import { gridOptionsBase, TypedHeaderValueGetterParams, TypedValueGetterParams } from 'view/components/datagrid'
import { Translation, Locale } from 'locale'
import { getNotionalFormatOptions } from 'view/shared/datagrid'

export enum TradingLimitsGridColumnId {
  TRADING_LIMITS_CURRENCY = 'TRADING_LIMITS_CURRENCY',
  TRADING_LIMITS_LIMIT = 'TRADING_LIMITS_LIMIT',
  TRADING_LIMITS_GROSS_UTILISATION = 'TRADING_LIMITS_GROSS_UTILISATION',
  TRADING_LIMITS_GROSS_AVAILABLE = 'TRADING_LIMITS_GROSS_AVAILABLE',
  TRADING_LIMITS_NET_UTILISATION = 'TRADING_LIMITS_NET_UTILISATION',
  TRADING_LIMITS_NET_AVAILABLE_BUY = 'TRADING_LIMITS_NET_AVAILABLE_BUY',
  TRADING_LIMITS_NET_AVAILABLE_SELL = 'TRADING_LIMITS_NET_AVAILABLE_SELL'
}

const amountFormat = (params: ValueFormatterParams) => {
  const formatOptions = getNotionalFormatOptions(params.context.userPreference.amountFormat)
  return Locale.formatNumber(params.value, formatOptions)
}

export const formatHeaderUnits = (amountFormat: AmountFormat): string => {
  switch (amountFormat) {
    case AmountFormat.MILLIONS:
      return " (M's)"
    case AmountFormat.THOUSANDS:
      return " (000's)"
    default:
      return ''
  }
}

function tradingLimitHeaderValueGetter(params: TypedHeaderValueGetterParams<CurrencyTradingLimitDetail>) {
  return Locale.translate(Translation.TC_TRADING_LIMIT) + formatHeaderUnits(params.context.userPreference.amountFormat)
}
function tradingGrossUtilisationHeaderValueGetter(params: TypedHeaderValueGetterParams<any>) {
  return Locale.translate(Translation.TC_GROSS_UTILISATION) + formatHeaderUnits(params.context.userPreference.amountFormat)
}
function tradingGrossAvailableHeaderValueGetter(params: TypedHeaderValueGetterParams<any>) {
  return Locale.translate(Translation.TC_GROSS_AVAILABLE) + formatHeaderUnits(params.context.userPreference.amountFormat)
}
function tradingNetUtilisationHeaderValueGetter(params: TypedHeaderValueGetterParams<any>) {
  return Locale.translate(Translation.TC_NET_UTILISATION) + formatHeaderUnits(params.context.userPreference.amountFormat)
}
function tradingNetAvailableBuyHeaderValueGetter(params: TypedHeaderValueGetterParams<any>) {
  return Locale.translate(Translation.TC_NET_AVAILABLE_BUY) + formatHeaderUnits(params.context.userPreference.amountFormat)
}
function tradingNetAvailableSellHeaderValueGetter(params: TypedHeaderValueGetterParams<any>) {
  return Locale.translate(Translation.TC_NET_AVAILABLE_SELL) + formatHeaderUnits(params.context.userPreference.amountFormat)
}

export const tradingLimitsColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: TradingLimitsGridColumnId.TRADING_LIMITS_CURRENCY,
    headerName: Locale.translate(Translation.TC_CURRENCY),
    width: 200,
    filter: 'agSetColumnFilter',
    valueGetter: (params: TypedValueGetterParams<CurrencyTradingLimitDetail>) => params.data && params.data.currency
  },
  {
    colId: TradingLimitsGridColumnId.TRADING_LIMITS_LIMIT,
    headerName: Locale.translate(Translation.TC_TRADING_LIMIT),
    headerValueGetter: tradingLimitHeaderValueGetter,
    width: 200,
    filter: 'agSetColumnFilter',
    valueFormatter: amountFormat,
    valueGetter: (params: TypedValueGetterParams<CurrencyTradingLimitDetail>) => params.data && params.data.limit
  },
  {
    colId: TradingLimitsGridColumnId.TRADING_LIMITS_GROSS_UTILISATION,
    headerName: Locale.translate(Translation.TC_GROSS_UTILISATION),
    headerValueGetter: tradingGrossUtilisationHeaderValueGetter,
    width: 200,
    filter: 'agSetColumnFilter',
    valueFormatter: amountFormat,
    valueGetter: (params: TypedValueGetterParams<CurrencyTradingLimitDetail>) => params.data && params.data.utilisationGross
  },
  {
    colId: TradingLimitsGridColumnId.TRADING_LIMITS_GROSS_AVAILABLE,
    headerName: Locale.translate(Translation.TC_GROSS_AVAILABLE),
    headerValueGetter: tradingGrossAvailableHeaderValueGetter,
    width: 200,
    filter: 'agSetColumnFilter',
    valueFormatter: amountFormat,
    valueGetter: (params: TypedValueGetterParams<CurrencyTradingLimitDetail>) => params.data && params.data.availableGross
  },
  {
    colId: TradingLimitsGridColumnId.TRADING_LIMITS_NET_UTILISATION,
    headerName: Locale.translate(Translation.TC_NET_UTILISATION),
    headerValueGetter: tradingNetUtilisationHeaderValueGetter,
    width: 200,
    filter: 'agSetColumnFilter',
    valueFormatter: amountFormat,
    valueGetter: (params: TypedValueGetterParams<CurrencyTradingLimitDetail>) => params.data && params.data.utilisationNet
  },
  {
    colId: TradingLimitsGridColumnId.TRADING_LIMITS_NET_AVAILABLE_SELL,
    headerName: Locale.translate(Translation.TC_NET_AVAILABLE_SELL),
    headerValueGetter: tradingNetAvailableSellHeaderValueGetter,
    width: 200,
    filter: 'agSetColumnFilter',
    valueGetter: (params: TypedValueGetterParams<CurrencyTradingLimitDetail>) => {
      const formatOptions = getNotionalFormatOptions(params.context.userPreference.amountFormat)
      return Locale.formatNumber(params.data && params.data.availableNetSell, formatOptions)
    }
  },
  {
    colId: TradingLimitsGridColumnId.TRADING_LIMITS_NET_AVAILABLE_BUY,
    headerName: Locale.translate(Translation.TC_NET_AVAILABLE_BUY),
    headerValueGetter: tradingNetAvailableBuyHeaderValueGetter,
    width: 200,
    filter: 'agSetColumnFilter',
    valueGetter: (params: TypedValueGetterParams<CurrencyTradingLimitDetail>) => {
      const formatOptions = getNotionalFormatOptions(params.context.userPreference.amountFormat)

      return Locale.formatNumber(params.data && params.data.availableNetBuy, formatOptions)
    }
  }
]

export const tradingLimitsGridOptions: GridOptions<TradingEntityMetaDetail> = {
  ...gridOptionsBase
}
